/**
 * Servicio para tracking de tiempo de interacción
 * Basado en ReadTime.cs
 *
 * El tiempo se mide como TIEMPO ACTIVO, no reloj de pared: se acumulan los
 * intervalos entre eventos de actividad (input del usuario, respuestas del
 * asistente), y cualquier intervalo sin actividad se recorta a
 * INACTIVITY_THRESHOLD_SECONDS. Con la pestaña oculta no corre el contador.
 *
 * Motivo: los estudiantes dejan la pestaña abierta durante horas y el viejo
 * cálculo (Date.now() - inicio de la app) producía time_total absurdos en
 * `conversations` (se encontró una sesión de 1,244 min con 22 mensajes). El
 * reporte semanal mitigaba con un tope de 2h por sesión, pero el dato de
 * origen seguía mal.
 */

export interface TimeData {
  id: string;
  available: boolean;
  created: string;
  modified: string;
  assistant_id: string;
  email: string;
  time: number;
}

/** Máximo de segundos sin actividad que cuentan como tiempo de uso. */
const INACTIVITY_THRESHOLD_SECONDS = 5 * 60;

export class InteractionTimeService {
  private accumulatedSeconds: number = 0;
  /** Timestamp (ms) de la última actividad; null = contador en pausa (pestaña oculta). */
  private lastActivityAt: number | null = null;
  private updateTimeID: string | null = null;
  private initialized: boolean = false;

  /**
   * Inicializar tracking de tiempo de interaccion (100% local).
   *
   * Antes se sincronizaba con la API de mentoraibot.com (getTimeFromAPI /
   * createTimeRecord), pero ese backend ya no existe en Galia 1.5. Ahora el
   * tiempo se calcula totalmente desde el cliente.
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;
    this.lastActivityAt = Date.now();

    const onActivity = () => this.registerActivity();
    window.addEventListener('pointerdown', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity, { passive: true });
    window.addEventListener('touchstart', onActivity, { passive: true });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // Cerrar el segmento en curso (recortado al umbral) y pausar.
        this.closeCurrentSegment();
      } else {
        // Reanudar sin acumular el tiempo que la pestaña estuvo oculta.
        this.lastActivityAt = Date.now();
      }
    });
  }

  /**
   * Registrar actividad (input del usuario o respuesta del asistente).
   * Acumula el intervalo desde la actividad anterior, recortado al umbral de
   * inactividad, y reinicia el segmento.
   */
  registerActivity(): void {
    const now = Date.now();
    if (this.lastActivityAt === null) {
      // Estaba en pausa (pestaña oculta): reanudar sin acumular.
      this.lastActivityAt = now;
      return;
    }
    const gapSeconds = (now - this.lastActivityAt) / 1000;
    this.accumulatedSeconds += Math.min(gapSeconds, INACTIVITY_THRESHOLD_SECONDS);
    this.lastActivityAt = now;
  }

  private closeCurrentSegment(): void {
    if (this.lastActivityAt === null) return;
    const gapSeconds = (Date.now() - this.lastActivityAt) / 1000;
    this.accumulatedSeconds += Math.min(gapSeconds, INACTIVITY_THRESHOLD_SECONDS);
    this.lastActivityAt = null;
  }

  /**
   * Obtener tiempo activo transcurrido (segundos)
   */
  getTimeElapsed(): number {
    let currentSegment = 0;
    if (this.lastActivityAt !== null) {
      const gapSeconds = (Date.now() - this.lastActivityAt) / 1000;
      currentSegment = Math.min(gapSeconds, INACTIVITY_THRESHOLD_SECONDS);
    }
    return Math.floor(this.accumulatedSeconds + currentSegment);
  }

  /**
   * Obtener tiempo en formato horas:minutos
   */
  getTimeFormatted(): string {
    const totalSeconds = this.getTimeElapsed();
    const horas = Math.floor(totalSeconds / 3600);
    const minutos = Math.floor((totalSeconds - horas * 3600) / 60);
    return `${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}`;
  }

  /**
   * Actualizar tiempo en servidor
   * NOTA: Esta funcionalidad ha sido deshabilitada - ya no se hacen llamadas a mentoraibot.com
   */
  async updateTime(): Promise<void> {
    // Funcionalidad deshabilitada - ya no se hacen llamadas a mentoraibot.com
  }

  getUpdateTimeID(): string | null {
    return this.updateTimeID;
  }
}

export const interactionTimeService = new InteractionTimeService();
export default interactionTimeService;
